/**
 * MediaKind (MK.IO) ストリーミング URL / DRM 設定の一元管理。
 *
 * ブラウザごとの manifest フォーマット・暗号化方式・DRM キーシステムの
 * 組み合わせをここで一括決定する:
 *
 * | ブラウザ          | format        | encryption | source | DRM                        |
 * |-------------------|---------------|------------|--------|----------------------------|
 * | Safari / iOS      | m3u8-cmaf     | cbcs-aapl  | hls    | fairplay                   |
 * | Edge (Win/Mac)    | mpd-time-cmaf | cenc       | dash   | widevine                   |
 * | その他 (Chrome等) | mpd-time-cmaf | cenc       | dash   | widevine                   |
 *
 * Edge / Chrome では PlayReady を提示せず Widevine のみに固定するが、
 * preferredKeySystems は指定せず、MKPlayer / ブラウザ側の自然な選択に任せる。
 * PlayReady がハードウェア DRM (SL3000) を選択した際に、HDCP 非対応の
 * 表示経路（外部モニタ・RDP 等）で映像のみ黒画面になる問題を避けるため。
 */

export interface DrmProfile {
  format: 'm3u8-cmaf' | 'mpd-time-cmaf';
  encryption: 'cbcs-aapl' | 'cenc';
  sourceKey: 'hls' | 'dash';
  keySystems: Array<'widevine' | 'playready' | 'fairplay'>;
}

export const DRM_BASE_URL = 'https://ottapp-appgw-amp.prodc.mkio.tv3cloud.com/drm';

const FAIRPLAY_CERTIFICATE_URL =
  `${DRM_BASE_URL}/fairplay-cert?ownerUid=azuki` +
  '&policy_id=fb513107-6f15-4a0c-87a8-b26476fe4822' +
  '&subscription_id=cd04d0b7-917c-4368-9aee-155cca623c1c';

/**
 * UserAgent からブラウザ別の DRM プロファイルを決定する。
 */
export function detectDrmProfile(userAgent?: string): DrmProfile {
  const ua = userAgent ?? (typeof window !== 'undefined' ? window.navigator.userAgent : '');
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isIos = /iPad|iPhone/i.test(ua);
  const isEdge = ua.indexOf('Edg') > -1;

  if (isIos || isSafari) {
    return {
      format: 'm3u8-cmaf',
      encryption: 'cbcs-aapl',
      sourceKey: 'hls',
      keySystems: ['fairplay'],
    };
  }
  if (isEdge) {
    return {
      format: 'mpd-time-cmaf',
      encryption: 'cenc',
      sourceKey: 'dash',
      keySystems: ['widevine'],
    };
  }
  return {
    format: 'mpd-time-cmaf',
    encryption: 'cenc',
    sourceKey: 'dash',
    keySystems: ['widevine'],
  };
}

/**
 * MediaKind の manifest URL をプロファイルに合わせて書き換える。
 *
 * 対応パターン:
 * - `.../manifest(format=...,encryption=...)` （クエリ・ハッシュ付き含む）
 * - `.../manifest` （括弧なし）
 *
 * 書き換えできない URL は warn を出してそのまま返す（サイレント失敗にしない）。
 */
export function rewriteManifestUrl(url: string | null | undefined, profile: DrmProfile): string | null {
  if (!url) {
    return null;
  }

  const match = url.match(/^([^?#]*)([?#].*)?$/);
  const path = match?.[1] ?? url;
  const suffix = match?.[2] ?? '';
  const replacement = `manifest(format=${profile.format},encryption=${profile.encryption})`;

  if (/manifest\([^)]*\)$/.test(path)) {
    return path.replace(/manifest\([^)]*\)$/, replacement) + suffix;
  }
  if (/\/manifest$/i.test(path)) {
    return path.replace(/manifest$/i, replacement) + suffix;
  }

  console.warn('[streaming-url] manifest URL を書き換えできませんでした:', url);
  return url;
}

/**
 * MKPlayer `load()` に渡す sourceConfig を生成する。
 *
 * - ブラウザに合致する単一のソースキー（hls または dash）のみ設定する。
 *   両方渡すと MKPlayer が encryption 不整合の URL を試行して失敗するため。
 * - `Bearer=` は MK.IO の規約（`Bearer ` ではない）。
 */
export function buildMkSourceConfig(
  streamingURL: string,
  streamingToken: string,
  userAgent?: string,
): { sourceConfig: Record<string, unknown>; profile: DrmProfile; url: string } {
  const profile = detectDrmProfile(userAgent);
  const url = rewriteManifestUrl(streamingURL, profile) ?? streamingURL;
  const headers = { Authorization: 'Bearer=' + streamingToken };
  const drm: Record<string, unknown> = {};

  if (profile.keySystems.includes('widevine')) {
    drm['widevine'] = {
      LA_URL: `${DRM_BASE_URL}/widevine-license?ownerUid=azuki`,
      headers,
      videoRobustness: 'SW_SECURE_CRYPTO',
      audioRobustness: 'SW_SECURE_CRYPTO',
    };
  }
  if (profile.keySystems.includes('playready')) {
    drm['playready'] = {
      LA_URL: `${DRM_BASE_URL}/playready-license?ownerUid=azuki`,
      headers,
    };
  }
  if (profile.keySystems.includes('fairplay')) {
    drm['fairplay'] = {
      LA_URL: `${DRM_BASE_URL}/fairplay-license?ownerUid=azuki`,
      certificateURL: FAIRPLAY_CERTIFICATE_URL,
      headers,
    };
  }

  const sourceConfig = {
    [profile.sourceKey]: url,
    drm,
  };

  return { sourceConfig, profile, url };
}
